// src/pages/admin/CourseLectures/lectures/Lecture09ApplicationReview.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${
        isOpen ? "border-[#009fe3]/50" : "border-gray-200"
      } bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
          isOpen ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${
            isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"
          }`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${
            isOpen ? "text-[#0369a1]" : "text-gray-800"
          }`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Field Row ─────────────────────────────────────────────────────
const FieldRow: React.FC<{
  icon: string;
  label: string;
  desc: string;
  example?: string;
  highlight?: boolean;
}> = ({ icon, label, desc, example, highlight }) => (
  <div
    className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
      highlight ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"
    }`}
  >
    <span className="text-base flex-shrink-0 mt-0.5">{icon}</span>
    <div className="flex-1 min-w-0">
      <p
        className={`text-sm font-bold ${
          highlight ? "text-amber-800" : "text-gray-800"
        }`}
      >
        {label}
      </p>
      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
      {example && (
        <code className="inline-block mt-1 text-[11px] bg-white border border-gray-200 rounded px-1.5 py-0.5 text-[#0369a1] font-mono">
          {example}
        </code>
      )}
    </div>
  </div>
);

// ── Doc Item ──────────────────────────────────────────────────────
const DocItem: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
    <span className="text-green-500 font-bold flex-shrink-0">✓</span>
    <p className="text-xs text-gray-700">{label}</p>
  </div>
);

// ── PAR Donut Diagram (from Lecture03) ───────────────────────────
const PARDiagram: React.FC = () => (
  <div className="flex flex-col sm:flex-row items-center gap-6 py-4">
    <div className="flex-shrink-0">
      <svg viewBox="0 0 220 220" width="200" height="200" className="mx-auto">
        {/* 03 — top (dark teal): 210° → 330° */}
        <path
          d="M 40.72 70.00 A 80 80 0 0 1 179.28 70.00 L 149.84 87.00 A 46 46 0 0 0 70.16 87.00 Z"
          fill="#00796b"
        />
        {/* 01 — bottom-left (light teal): 90° → 210° */}
        <path
          d="M 110.00 190.00 A 80 80 0 0 1 40.72 70.00 L 70.16 87.00 A 46 46 0 0 0 110.00 156.00 Z"
          fill="#4db6ac"
        />
        {/* 02 — bottom-right (medium teal): 330° → 90° */}
        <path
          d="M 179.28 70.00 A 80 80 0 0 1 110.00 190.00 L 110.00 156.00 A 46 46 0 0 0 149.84 87.00 Z"
          fill="#26a69a"
        />
        {/* White donut hole */}
        <circle cx="110" cy="110" r="46" fill="white" />
        {/* Segment number labels */}
        <text
          x="110"
          y="47"
          fill="white"
          fontSize="13"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          03
        </text>
        <text
          x="55.44"
          y="141.50"
          fill="white"
          fontSize="13"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          01
        </text>
        <text
          x="164.56"
          y="141.50"
          fill="white"
          fontSize="13"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          02
        </text>
      </svg>
    </div>
    <div className="flex flex-col gap-4 w-full">
      {[
        {
          num: "01",
          color: "bg-[#4db6ac]",
          label: "Problem",
          sub: "Was war die Herausforderung?",
        },
        {
          num: "02",
          color: "bg-[#26a69a]",
          label: "Action",
          sub: "Was hast du getan?",
        },
        {
          num: "03",
          color: "bg-[#00796b]",
          label: "Result",
          sub: "Was war das Ergebnis?",
        },
      ].map((item) => (
        <div key={item.num} className="flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-full ${item.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}
          >
            {item.num}
          </div>
          <div>
            <p className="text-sm font-bold text-gray-800">{item.label}</p>
            <p className="text-xs text-gray-500">{item.sub}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const Lecture09ApplicationReview: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Aufgaben aus Lektion 08 — erledigt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "Bewerbung Protokollieren!",
            "Mindestens 2 Bewerbungen am Tag!",
            "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "DIN 5008 — Dokumente scannen & sichern" },
          { num: "2", label: "Zeugnisse übersetzen — beglaubigte Übersetzung" },
          { num: "3", label: "PAR-Methode — Bewerbungsstrategie" },
          { num: "4", label: "Wen suchen Sie? — Stellenanzeige analysieren" },
          { num: "5", label: "Motivation & ATS" },
          { num: "6", label: "E-Mail-Adresse" },
          { num: "7", label: "How To — Application Tracker Felder (Teil 1)" },
          { num: "8", label: "How To — Application Tracker Felder (Teil 2)" },
          { num: "9", label: "How To — PDF Merge & Compress" },
          { num: "10", label: "Follow Up — 14-Tage-Regel" },
        ]}
      />

      {/* 01 — DIN 5008 */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="📄"
        title="DIN 5008 — Originaldokumente scannen & sichern"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 mb-4">
          <p className="text-sm font-bold text-blue-800 mb-1">
            Scan the Original Copy and Keep it in your drive and Computer
          </p>
          <p className="text-xs text-blue-700">
            Scanne alle Originaldokumente und speichere sie auf Google Drive{" "}
            <strong>und</strong> auf deinem Computer als Backup.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <DocItem label="Passport (Reisepass)" />
          <DocItem label="SSC Marksheet" />
          <DocItem label="SSC Certificate" />
          <DocItem label="HSC Marksheet / Diploma Mark Sheets" />
          <DocItem label="HSC Certificate / Diploma Certificates" />
          <DocItem label="Bachelor Marksheets + Certificate" />
          <DocItem label="Birth Certificate (Geburtsurkunde)" />
        </div>
        <InfoBox variant="tip" icon="☁️" title="Zwei Speicherorte">
          Google Drive für Zugriff von überall — lokale Kopie als Backup. Beide
          Orte = doppelte Sicherheit.
        </InfoBox>
      </Accordion>

      {/* 02 — Zeugnisse Übersetzen */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="🌐"
        title="Zeugnisse übersetzen — Beglaubigte Übersetzung (Dolmetscher)"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4">
          <p className="text-sm font-bold text-amber-800 mb-1">
            ⚠️ Nur staatlich anerkannte Übersetzer!
          </p>
          <p className="text-xs text-amber-700">
            Sende die Scan-Kopien an einen{" "}
            <strong>staatlich vereidigten Dolmetscher</strong> zur Übersetzung
            ins Deutsche. Nur beglaubigte Übersetzungen werden akzeptiert.
          </p>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Diese Dokumente müssen übersetzt werden:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          <DocItem label="SSC Marksheet" />
          <DocItem label="SSC Certificate" />
          <DocItem label="HSC Marksheet / Diploma Mark Sheets" />
          <DocItem label="HSC Certificate / Diploma Certificates" />
          <DocItem label="Bachelor Marksheets + Certificate" />
          <DocItem label="Birth Certificate (Geburtsurkunde)" />
        </div>
        <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3">
          <p className="text-xs font-bold text-purple-800 mb-1">
            📁 Reihenfolge im Bewerbungs-PDF
          </p>
          <p className="text-xs text-purple-700">
            Pro Dokument immer: <strong>Übersetzung zuerst</strong>, dann das
            Original dahinter.
          </p>
        </div>
      </Accordion>

      {/* 03 — PAR */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="🔄"
        title="PAR-Methode — Bewerbungsstrategie"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Die PAR-Methode (Problem, Action, Result) hilft dir, Eigenschaften im
          Anschreiben durch echte Beispiele zu beweisen — statt sie nur zu
          behaupten.
        </p>

        {/* PAR Donut Diagram */}
        <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-4 mb-4">
          <PARDiagram />
        </div>

        {/* Beispiel */}
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
          Beispiel — PAR: Zuverlässigkeit
        </p>
        <div className="rounded-xl border border-teal-200 bg-teal-50 p-4 mb-4">
          <div className="space-y-2 mb-3">
            {[
              {
                label: "P",
                text: "Bei einer Schul-Gruppenarbeit war es wichtig, dass jeder seinen Teil pünktlich abgibt.",
              },
              {
                label: "A",
                text: "Ich habe meine Aufgaben frühzeitig erledigt und mein Team regelmäßig informiert.",
              },
              {
                label: "R",
                text: "Dadurch konnten wir das Projekt rechtzeitig abgeben und gute Ergebnisse erzielen.",
              },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-2 text-sm">
                <span className="w-6 h-6 rounded-full bg-[#00796b] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {item.label}
                </span>
                <span className="text-teal-800">{item.text}</span>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-lg border border-teal-200 p-3">
            <p className="text-xs font-bold text-gray-500 mb-1">
              Daraus wird ein Anschreiben-Satz:
            </p>
            <p className="text-sm text-gray-700 italic">
              "Meine Zuverlässigkeit habe ich in der Schule bewiesen: Als bei
              einer Gruppenarbeit Termindruck entstand, erledigte ich meinen
              Teil frühzeitig und hielt das Team auf dem Laufenden — das Projekt
              wurde pünktlich mit gutem Ergebnis abgegeben."
            </p>
          </div>
        </div>

        <InfoBox
          variant="info"
          icon="🎯"
          title="Eigene PAR-Geschichten entwickeln"
        >
          Erstelle PAR-Geschichten für: Teamarbeit, Lernbereitschaft,
          Pünktlichkeit, Problemlösung — je nach Ausbildungsberuf anpassen.
        </InfoBox>
      </Accordion>

      {/* 04 — Wen suchen Sie */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="🔍"
        title="Wen suchen Sie? — Stellenanzeige analysieren"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Bevor du schreibst, lies die Stellenanzeige genau durch. Der
          Arbeitgeber sagt dir direkt, wen er sucht — nutze das.
        </p>
        <div className="space-y-3">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm font-bold text-blue-800 mb-2">
              📋 Was du aus der Anzeige herauslesen musst
            </p>
            <div className="space-y-1.5">
              {[
                "Welche Fähigkeiten und Eigenschaften werden gefordert?",
                "Welche Aufgaben wirst du übernehmen?",
                "Was betont das Unternehmen an sich selbst?",
                "Welche Keywords tauchen mehrfach auf?",
              ].map((tip, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span>
                  <p className="text-xs text-blue-700">{tip}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-bold text-green-800 mb-2">
              ✅ So verwendest du die Anzeige im Anschreiben
            </p>
            <div className="space-y-1.5">
              {[
                "Spiegele die Keywords aus der Anzeige in deinem Anschreiben (ATS-optimiert)",
                "Beweise jede geforderte Eigenschaft mit einer PAR-Geschichte",
                "Zeige, dass du die Aufgaben kennst und darauf vorbereitet bist",
              ].map((tip, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span>
                  <p className="text-xs text-green-700">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <InfoBox variant="warning" icon="⚠️" title="Kein Copy-Paste!">
          Verwende die Keywords aus der Anzeige — aber schreibe eigene Sätze.
          Wort-für-Wort-Kopieren fällt Recruitern sofort auf.
        </InfoBox>
      </Accordion>

      {/* 05 — Motivation & ATS */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="💡"
        title="Motivation & ATS"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-bold text-green-800 mb-2">
              💚 Motivation — Warum genau diese Firma?
            </p>
            <p className="text-xs text-green-700 mb-2">
              Der stärkste Teil des Anschreibens. Zeige, dass du die Firma
              kennst — nicht nur den Beruf.
            </p>
            <div className="space-y-1.5">
              {[
                "Recherchiere: Projekte, Werte, News der Firma (Website Über uns)",
                "Nenne konkret, was dich an dieser Firma begeistert",
                "Verknüpfe deine Stärken mit dem Bedarf der Firma",
              ].map((tip, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span>
                  <p className="text-xs text-green-700">{tip}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
            <p className="text-sm font-bold text-purple-800 mb-2">
              🤖 ATS — Applicant Tracking System
            </p>
            <p className="text-xs text-purple-700 mb-2">
              Viele Firmen nutzen Software, die Bewerbungen automatisch scannt.
              Dein Anschreiben muss ATS-kompatibel sein.
            </p>
            <div className="space-y-1.5">
              {[
                "Exakte Keywords aus der Stellenanzeige verwenden",
                "Keine Tabellen oder Grafiken im Anschreiben",
                "Standard-Schriftart: Arial, Calibri oder Times New Roman",
                "Dateiname: Bewerbung_Beruf_Vorname_Nachname.pdf",
              ].map((tip, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span>
                  <p className="text-xs text-purple-700">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Accordion>

      {/* 06 — E-Mail-Adresse */}
      <Accordion
        id="06"
        num="ABSCHNITT 06"
        icon="📧"
        title="Professionelle E-Mail-Adresse"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Deine E-Mail-Adresse ist der erste Eindruck — sie muss professionell
          sein.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="bg-red-50 rounded-xl border border-red-200 px-4 py-3">
            <p className="text-xs font-bold text-red-700 mb-2">❌ Falsch</p>
            <p className="text-xs font-mono text-red-600">
              coolguy2002@gmail.com
            </p>
            <p className="text-xs font-mono text-red-600">
              superman_bd@yahoo.com
            </p>
            <p className="text-xs font-mono text-red-600">
              md.rahim123@hotmail.com
            </p>
          </div>
          <div className="bg-green-50 rounded-xl border border-green-200 px-4 py-3">
            <p className="text-xs font-bold text-green-700 mb-2">✅ Richtig</p>
            <p className="text-xs font-mono text-green-700">
              vorname.nachname@gmail.com
            </p>
            <p className="text-xs font-mono text-green-700">
              v.nachname@gmail.com
            </p>
            <p className="text-xs font-mono text-green-700">
              vorname.nachname@outlook.de
            </p>
          </div>
        </div>
        <InfoBox variant="tip" icon="💡" title="Format">
          Verwende immer <strong>vorname.nachname</strong> oder{" "}
          <strong>v.nachname</strong> — @gmail.com oder @outlook.de bevorzugt.
        </InfoBox>
      </Accordion>

      {/* 07 — Tracker Felder Teil 1 */}
      <Accordion
        id="07"
        num="ABSCHNITT 07"
        icon="📊"
        title="How To — Application Tracker Felder ausfüllen (Teil 1)"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          So füllst du jeden Eintrag im G2G Application Tracker korrekt aus:
        </p>
        <div className="space-y-2">
          <FieldRow
            icon="🏢"
            label="Company Name"
            desc="এখানে আপনি যেই কোম্পানিতে আবেদন করেছেন, সেই কোম্পানির নাম লিখবেন।"
            example="Charité Berlin · Werkzeug GmbH"
          />
          <FieldRow
            icon="💼"
            label="Position / Program"
            desc="কোন পজিশন/প্রোগ্রামের জন্য আবেদন করেছেন।"
            example="Ausbildung als Pflegefachmann · Ausbildung als Verkäufer"
          />
          <FieldRow
            icon="📅"
            label="Date Applied"
            desc="আপনি যে দিন আবেদন করেছেন, সেই তারিখ লিখবেন।"
            example="19.11.2025 (Format: DD.MM.YYYY)"
          />
          <FieldRow
            icon="🔄"
            label="Application Status"
            desc="আপনার আবেদন কোন স্টেজে আছে। Dropdown থেকে বাছাই করবেন।"
            example="Beworben · Einladung · Abgelehnt · Warteliste"
          />
          <FieldRow
            icon="📬"
            label="Feedback Received"
            desc="কোম্পানির কাছ থেকে কোনো উত্তর পেয়েছেন কি না।"
          />
          <FieldRow
            icon="⏰"
            label="Next Action"
            desc="এরপর আপনাকে কী করতে হবে। প্রতিটি ধাপের জন্য ১৪ দিনের প্যারামিটার সেট করুন।"
            example="Follow-up E-Mail senden · Anrufen"
          />
        </div>
      </Accordion>

      {/* 08 — Tracker Felder Teil 2 */}
      <Accordion
        id="08"
        num="ABSCHNITT 08"
        icon="📋"
        title="How To — Application Tracker Felder ausfüllen (Teil 2)"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="space-y-2 mb-4">
          <FieldRow
            icon="📆"
            label="Next Action Due"
            desc="পরবর্তী কাজটি কোন তারিখের আগে করতে হবে।"
            example="25.11.2025 → Follow-up E-Mail senden"
          />
          <FieldRow
            icon="👤"
            label="Contact Person"
            desc="কোম্পানির Ansprechpartner নাম।"
            example="Reinhart Müller"
          />
          <FieldRow
            icon="📧"
            label="Contact Email"
            desc="যার কাছে আবেদন গেছে বা যিনি রিক্রুটার, তার ইমেইল।"
            example="hr@company.de"
          />
          <FieldRow
            icon="📝"
            label="Notes"
            desc="যেকোনো অতিরিক্ত নোট বা গুরুত্বপূর্ণ তথ্য লিখবেন। যদি ফোন নম্বর থাকে তাহলে সেটাও লিখে রাখুন।"
          />
          <FieldRow
            icon="📦"
            label="Cover Letter (PDF — Max 2MB)"
            desc="এখানে আপনার Cover Letter + Stellenausschreibung PDF merge করা ফাইলটি আপলোড করবেন। প্রয়োজনে Compress করুন।"
            highlight
          />
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-sm font-bold text-red-800 flex items-center gap-2">
            <span>💾</span> DO NOT forget to SAVE!
          </p>
          <p className="text-xs text-red-700 mt-1">
            Nach dem Ausfüllen aller Felder unbedingt speichern — sonst gehen
            deine Daten verloren.
          </p>
        </div>
      </Accordion>

      {/* 09 — Merge & Compress */}
      <Accordion
        id="09"
        num="ABSCHNITT 09"
        icon="🗜️"
        title="How To — PDF zusammenführen & komprimieren"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Das Cover-Letter-PDF muss Anschreiben + Stellenausschreibung zusammen
          enthalten — max. 2 MB.
        </p>
        <div className="space-y-2 mb-4">
          {[
            {
              step: "1",
              icon: "🌐",
              label: "Stellenausschreibung als PDF speichern",
              desc: "Im Browser: Strg + P → Als PDF speichern wählen",
              cls: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              step: "2",
              icon: "📄",
              label: "Anschreiben als PDF bereitstellen",
              desc: "Dein fertiges Anschreiben bereits als PDF vorhanden",
              cls: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              step: "3",
              icon: "🔗",
              label: "Beide PDFs zusammenführen (Merge)",
              desc: "Anschreiben zuerst, dann Stellenausschreibung — Merge klicken und herunterladen",
              cls: "bg-green-50 border-green-200 text-green-800",
            },
            {
              step: "4",
              icon: "📦",
              label: "Falls über 2MB: Komprimieren",
              desc: "Bei ilovepdf.com/compress_pdf hochladen — oder google: Compress PDF",
              cls: "bg-amber-50 border-amber-200 text-amber-800",
            },
          ].map((item) => (
            <div
              key={item.step}
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${item.cls}`}
            >
              <div className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-sm font-extrabold flex-shrink-0">
                {item.step}
              </div>
              <div>
                <p className="text-sm font-bold">
                  {item.icon} {item.label}
                </p>
                <p className="text-xs opacity-80 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-gray-900 rounded-xl px-4 py-3 text-xs">
          <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-2">
            Empfohlene Tools:
          </p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">🔗</span>
              <a
                href="https://www.ilovepdf.com/merge_pdf"
                target="_blank"
                rel="noreferrer"
                className="text-[#009fe3] underline font-semibold"
              >
                ilovepdf.com/merge_pdf
              </a>
              <span className="text-gray-500">— PDFs zusammenführen</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">🗜️</span>
              <a
                href="https://www.ilovepdf.com/compress_pdf"
                target="_blank"
                rel="noreferrer"
                className="text-[#009fe3] underline font-semibold"
              >
                ilovepdf.com/compress_pdf
              </a>
              <span className="text-gray-500">— PDF-Größe reduzieren</span>
            </div>
            <p className="text-gray-500 text-[10px] mt-1">
              Alternativ: Google → "Merge PDF" oder "Compress PDF" |
              smallpdf.com
            </p>
          </div>
        </div>
      </Accordion>

      {/* 10 — Follow Up */}
      <Accordion
        id="10"
        num="ABSCHNITT 10"
        icon="📬"
        title="Follow Up — 14-Tage-Regel"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Setze das Follow-up-Datum immer auf 14 Tage (2 Wochen) nach dem
          Absenden der Bewerbung.
        </p>

        <div className="relative pl-6 space-y-3 mb-4">
          <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#009fe3] via-amber-400 to-red-400 rounded-full" />
          {[
            {
              day: "Tag 0",
              label: "Bewerbung abgeschickt",
              desc: "Im Tracker eintragen — Next Action Due in 14 Tagen setzen",
              color: "bg-blue-50 border-blue-200 text-blue-800",
              dot: "bg-[#009fe3]",
            },
            {
              day: "Tag 14",
              label: "Follow-up per E-Mail",
              desc: "Keine Antwort erhalten? Höfliche Follow-up-E-Mail senden",
              color: "bg-amber-50 border-amber-200 text-amber-800",
              dot: "bg-amber-400",
            },
            {
              day: "Tag 21+",
              label: "Follow-up per Telefon",
              desc: "Keine Antwort auf E-Mail? Jetzt anrufen via Skype oder Mobilvoip",
              color: "bg-red-50 border-red-200 text-red-800",
              dot: "bg-red-400",
            },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div
                className={`absolute left-0.5 w-3 h-3 rounded-full border-2 border-white ${item.dot}`}
                style={{ top: `${i * 5.5 + 0.5}rem` }}
              />
              <div
                className={`flex-1 rounded-xl border px-3 py-2.5 ${item.color}`}
              >
                <p className="text-[10px] font-bold uppercase tracking-wide opacity-60">
                  {item.day}
                </p>
                <p className="text-sm font-bold">{item.label}</p>
                <p className="text-xs opacity-80 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-3">
          <p className="text-sm font-bold text-blue-800 mb-3">
            📋 Wie du richtig nachfragst
          </p>
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-sm text-blue-700">
              <span className="flex-shrink-0 font-bold text-green-600 mt-0.5">
                ↗
              </span>
              <span>
                <strong>Angeben (Give):</strong> Application Reference — wann du
                dich beworben hast, für welche Stelle
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm text-blue-700">
              <span className="flex-shrink-0 font-bold text-[#009fe3] mt-0.5">
                ↙
              </span>
              <span>
                <strong>Fragen (Ask):</strong> Current Status of the application
                — höflich und professionell
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 mb-3">
          <p className="text-sm font-bold text-purple-800 mb-2">
            📱 Apps zum Anrufen aus dem Ausland
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              {
                name: "Skype",
                color: "bg-blue-100 text-blue-800 border-blue-200",
              },
              {
                name: "Mobilvoip",
                color: "bg-orange-100 text-orange-800 border-orange-200",
              },
            ].map((app) => (
              <span
                key={app.name}
                className={`text-xs font-bold px-2.5 py-1 rounded-full border ${app.color}`}
              >
                {app.name}
              </span>
            ))}
          </div>
          <p className="text-xs text-purple-600 mt-2 opacity-80">
            Credit card needed — günstig nach Deutschland telefonieren
          </p>
        </div>

        <InfoBox variant="tip" icon="⏱️" title="14-Tage-Regel">
          Setze für jede Bewerbung automatisch ein Follow-up-Datum auf{" "}
          <strong>14 Tage nach dem Absenden</strong>. Der Tracker zeigt dir,
          wann Aktion nötig ist.
        </InfoBox>
      </Accordion>

      {/* Fazit */}
      <Accordion
        id="fazit"
        num="FAZIT"
        icon="🏁"
        title="Fazit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              Du hast jetzt alle Werkzeuge für eine vollständige, professionelle
              Bewerbung.
            </p>
            <ul className="space-y-1">
              {[
                "Alle Originaldokumente gescannt und gesichert (Drive + Computer)",
                "Beglaubigte Übersetzungen beim vereidigten Dolmetscher beauftragt",
                "PAR-Methode: Eigenschaften durch echte Geschichten beweisen",
                "Stellenanzeige analysiert — Keywords für ATS und Motivation genutzt",
                "Professionelle E-Mail-Adresse: vorname.nachname@gmail.com",
                "Application Tracker vollständig ausgefüllt — alle Felder",
                "PDF Merge: Anschreiben + Stellenausschreibung, max. 2 MB",
                "14-Tage-Regel: E-Mail zuerst, dann Telefon",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="📋"
        title="Deine Aufgaben — Lektion 09"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>Fokus auf Qualität!</strong> — Jedes Anschreiben
                individuell mit PAR-Methode und Firmenmotivation
              </>,
              <>
                <strong>Update an G2G:</strong> Haltet uns auf dem Laufenden!
                Eingangsbestätigungen, Vorstellungsgespräche und Absagen melden
              </>,
              "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture09ApplicationReview;
